import { useCallback, useEffect, useMemo, useState } from "react";
import { Camera, Pencil, Plus, Search, Trash2, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import AutocompleteInput from "../components/AutocompleteInput";
import DatePickerField from "../components/DatePickerField";
import ExportMenu from "../components/ExportMenu";
import DateFilterBar, { makeInitialDateFilter, computeRange, isDateInRange } from "../components/DateFilterBar";
import { api } from "../lib/api";

const CATEGORIES = ["Servicios", "Producto", "Consultoría", "Renta", "Comisión", "Anticipo", "Sueldo", "Otros"];
const PAYMENT_METHODS = ["Transferencia", "Depósito", "Efectivo", "Crypto", "Otro"];
const STATUS_ORDER = ["cobrado", "por_cobrar"];
const STATUS_LABEL = { cobrado: "Cobrado", por_cobrar: "Por cobrar" };

const money = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 2 });

const emptyForm = {
  concept: "",
  amount: "",
  date: new Date().toISOString().slice(0, 10),
  category: "Servicios",
  payment_method: "Transferencia",
  client: "",
  note: "",
  has_invoice: false,
  payment_status: "cobrado",
};

export default function IngresosPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [incomes, setIncomes] = useState([]);
  const [query, setQuery] = useState("");
  const [dateFilter, setDateFilter] = useState(makeInitialDateFilter);
  const [categoryFilter, setCategoryFilter] = useState("Todas");
  const [paymentFilter, setPaymentFilter] = useState("Todos");
  const [statusFilter, setStatusFilter] = useState("Todos");
  const [clientFilter, setClientFilter] = useState("Todos");
  const [projectFilter, setProjectFilter] = useState("Todos");
  const [invoiceFilter, setInvoiceFilter] = useState("Todos");
  const [sort, setSort] = useState("newest");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [clientOptions, setClientOptions] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);

  const refresh = useCallback(async () => {
    try {
      const response = await api.get("/incomes", { withCredentials: true });
      setIncomes(response.data);
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos cargar los ingresos.");
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    api.get("/clients", { withCredentials: true })
      .then((response) => setClientOptions((response.data || []).map((item) => item.name).filter(Boolean)))
      .catch(() => setClientOptions([]));
  }, []);

  useEffect(() => {
    api.get("/projects", { withCredentials: true })
      .then((response) => setProjectOptions((response.data?.projects || []).map((p) => ({ id: p.id, name: p.name })).filter((p) => p.name)))
      .catch(() => setProjectOptions([]));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("new") === "1") {
      setEditingId(null);
      setForm(emptyForm);
      setError("");
      setModalOpen(true);
      navigate("/ingresos", { replace: true });
    }
  }, [location.search, navigate]);

  const createClient = async (name) => {
    try {
      const response = await api.post("/clients", { name }, { withCredentials: true });
      const created = response.data.name;
      setClientOptions((current) => [...current, created]);
      setNotice(`Cliente "${created}" creado.`);
      return created;
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos crear el cliente.");
      return null;
    }
  };

  const cycleStatus = async (income) => {
    const current = income.payment_status || "cobrado";
    const next = STATUS_ORDER[(STATUS_ORDER.indexOf(current) + 1) % STATUS_ORDER.length];
    try {
      const response = await api.put(`/incomes/${income.id}`, { payment_status: next }, { withCredentials: true });
      setIncomes((current) => current.map((item) => (item.id === income.id ? response.data : item)));
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos actualizar el estado.");
    }
  };

  const dateRange = useMemo(() => computeRange(dateFilter), [dateFilter]);

  const filtered = useMemo(() => {
    return incomes
      .filter((item) => {
        const raw = item.date || item.created_at || "";
        if (!raw) return dateFilter.preset === "all";
        return isDateInRange(raw, dateRange);
      })
      .filter((item) => categoryFilter === "Todas" || (item.category || "Otros") === categoryFilter)
      .filter((item) => paymentFilter === "Todos" || (item.payment_method || "") === paymentFilter)
      .filter((item) => statusFilter === "Todos" || (item.payment_status || "cobrado") === statusFilter)
      .filter((item) => clientFilter === "Todos" || (item.client || "") === clientFilter)
      .filter((item) => {
        if (projectFilter === "Todos") return true;
        if (projectFilter === "__none__") return !item.project_id;
        return item.project_id === projectFilter;
      })
      .filter((item) => {
        if (invoiceFilter === "Todos") return true;
        if (invoiceFilter === "Con factura") return !!item.has_invoice;
        return !item.has_invoice;
      })
      .filter((item) => {
        if (!query.trim()) return true;
        const term = query.trim().toLowerCase();
        return (item.concept || "").toLowerCase().includes(term)
          || (item.client || "").toLowerCase().includes(term)
          || (item.note || "").toLowerCase().includes(term);
      })
      .sort((a, b) => {
        const av = new Date(a.date || a.created_at || 0).getTime();
        const bv = new Date(b.date || b.created_at || 0).getTime();
        return sort === "oldest" ? av - bv : bv - av;
      });
  }, [incomes, dateFilter.preset, dateRange, categoryFilter, paymentFilter, statusFilter, clientFilter, projectFilter, invoiceFilter, query, sort]);

  const summary = useMemo(() => {
    if (!filtered.length) return { total: 0, biggest: 0, average: 0, topCategory: "—", count: 0, porCobrar: 0, porCobrarCount: 0 };
    const total = filtered.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    const biggest = filtered.reduce((max, item) => Math.max(max, Number(item.amount || 0)), 0);
    const average = total / filtered.length;
    const counts = filtered.reduce((acc, item) => {
      const key = item.category || "Otros";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    const topCategory = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || "—";
    const porCobrarItems = filtered.filter((item) => item.payment_status === "por_cobrar");
    return {
      total,
      biggest,
      average,
      topCategory,
      count: filtered.length,
      porCobrar: porCobrarItems.reduce((sum, item) => sum + Number(item.amount || 0), 0),
      porCobrarCount: porCobrarItems.length,
    };
  }, [filtered]);

  const activeFilterCount = [
    categoryFilter !== "Todas",
    paymentFilter !== "Todos",
    statusFilter !== "Todos",
    clientFilter !== "Todos",
    projectFilter !== "Todos",
    invoiceFilter !== "Todos",
  ].filter(Boolean).length;

  const clearAllFilters = () => {
    setCategoryFilter("Todas");
    setPaymentFilter("Todos");
    setStatusFilter("Todos");
    setClientFilter("Todos");
    setProjectFilter("Todos");
    setInvoiceFilter("Todos");
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError("");
    setModalOpen(true);
  };

  const openEdit = (income) => {
    setEditingId(income.id);
    setForm({
      concept: income.concept || "",
      amount: String(income.amount ?? ""),
      date: (income.date || income.created_at || "").slice(0, 10) || new Date().toISOString().slice(0, 10),
      category: income.category || "Otros",
      payment_method: income.payment_method || "Transferencia",
      client: income.client || "",
      note: income.note || "",
      has_invoice: Boolean(income.has_invoice),
      payment_status: income.payment_status || "cobrado",
    });
    setError("");
    setModalOpen(true);
  };

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    const amount = Number(form.amount);
    if (!form.concept.trim() || !amount || amount <= 0) {
      setError("Concepto y monto son obligatorios.");
      return;
    }
    try {
      if (editingId) {
        await api.put(`/incomes/${editingId}`, {
          concept: form.concept,
          amount,
          date: form.date,
          category: form.category,
          payment_method: form.payment_method,
          client: form.client,
          has_invoice: form.has_invoice,
          note: form.note,
          payment_status: form.payment_status,
        }, { withCredentials: true });
        setNotice("Ingreso actualizado.");
      } else {
        const body = new FormData(event.currentTarget);
        body.set("type", "income");
        body.set("note", form.concept);
        body.set("details", form.note);
        body.set("amount", String(amount));
        body.set("date", form.date);
        body.set("category", form.category);
        body.set("payment_method", form.payment_method);
        body.set("client", form.client);
        body.set("has_invoice", form.has_invoice ? "true" : "false");
        await api.post("/smart-input/manual-file", body, { withCredentials: true });
        setNotice("Ingreso guardado.");
      }
      setModalOpen(false);
      setForm(emptyForm);
      setEditingId(null);
      refresh();
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos guardar el ingreso.");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/incomes/${deleteTarget.id}`, { withCredentials: true });
      setNotice("Ingreso eliminado.");
      setDeleteTarget(null);
      refresh();
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos eliminar el ingreso.");
    }
  };

  const scanReceipt = () => navigate("/importar");

  return (
    <AppShell>
      <div className="page expenses-page incomes-page">
        <header className="expenses-head">
          <div>
            <small className="eyebrow">INGRESOS</small>
            <h1>Ingresos, <em>siempre a la vista.</em></h1>
          </div>
          <div className="expenses-head-actions">
            <ExportMenu resource="incomes" testid="incomes-export" label="Exportar" />
            <button type="button" className="expenses-scan-button" data-testid="incomes-scan-button" onClick={scanReceipt} disabled={scanning}>
              <Camera size={17} />
              {scanning ? "Analizando..." : "Escanear documento"}
            </button>
            <button type="button" className="expenses-add-button incomes-add-primary" data-testid="incomes-add-button" onClick={openCreate}>
              <Plus size={17} />
              Agregar Ingreso
            </button>
          </div>
        </header>

        {notice && <p className="expenses-notice" data-testid="incomes-notice">{notice}</p>}
        {error && <p className="expenses-error" data-testid="incomes-error">{error}</p>}

        <DateFilterBar value={dateFilter} onChange={setDateFilter} testid="incomes-date-filter" />

        <section className="expenses-summary" data-testid="incomes-summary">
          <article data-testid="summary-total"><small>TOTAL INGRESOS</small><strong>{money.format(summary.total)}</strong><span>{summary.count} movimiento{summary.count === 1 ? "" : "s"}</span></article>
          <article data-testid="summary-por-cobrar"><small>POR COBRAR</small><strong>{money.format(summary.porCobrar)}</strong><span>{summary.porCobrarCount} pendiente{summary.porCobrarCount === 1 ? "" : "s"}</span></article>
          <article data-testid="summary-biggest"><small>MAYOR INGRESO</small><strong>{money.format(summary.biggest)}</strong></article>
          <article data-testid="summary-average"><small>PROMEDIO</small><strong>{money.format(summary.average)}</strong></article>
          <article data-testid="summary-top-category"><small>CATEGORÍA MÁS USADA</small><strong>{summary.topCategory}</strong></article>
          <article data-testid="summary-count"><small>MOVIMIENTOS</small><strong>{summary.count}</strong><span>{dateRange.label || "Todo"}</span></article>
        </section>

        <section className="adv-filters" data-testid="incomes-adv-filters">
          <span className="adv-filter-group">Categoría:
            <select data-testid="incomes-category-filter" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
              <option>Todas</option>
              {CATEGORIES.map((cat) => <option key={cat}>{cat}</option>)}
            </select>
          </span>
          <span className="adv-filter-group">Método:
            <select data-testid="incomes-payment-filter" value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)}>
              <option>Todos</option>
              {PAYMENT_METHODS.map((m) => <option key={m}>{m}</option>)}
            </select>
          </span>
          <span className="adv-filter-group">Estado:
            <select data-testid="incomes-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="Todos">Todos</option>
              <option value="cobrado">Cobrado</option>
              <option value="por_cobrar">Por cobrar</option>
            </select>
          </span>
          <span className="adv-filter-group">Cliente:
            <select data-testid="incomes-client-filter" value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}>
              <option>Todos</option>
              {clientOptions.map((c) => <option key={c}>{c}</option>)}
            </select>
          </span>
          <span className="adv-filter-group">Proyecto:
            <select data-testid="incomes-project-filter" value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)}>
              <option value="Todos">Todos</option>
              <option value="__none__">Sin proyecto</option>
              {projectOptions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </span>
          <span className="adv-filter-group">Factura:
            <select data-testid="incomes-invoice-filter" value={invoiceFilter} onChange={(e) => setInvoiceFilter(e.target.value)}>
              <option value="Todos">Todas</option>
              <option value="Con factura">Con factura</option>
              <option value="Sin factura">Sin factura</option>
            </select>
          </span>
          {activeFilterCount > 0 && (
            <button type="button" className="adv-clear-all" onClick={clearAllFilters} data-testid="incomes-clear-filters">
              Limpiar filtros ({activeFilterCount})
            </button>
          )}
        </section>

        <section className="expenses-controls">
          <label className="expenses-search" htmlFor="incomes-search-input">
            <Search size={17} />
            <input id="incomes-search-input" data-testid="incomes-search-input" placeholder="Buscar por concepto, cliente o nota" value={query} onChange={(event) => setQuery(event.target.value)} />
          </label>
          <select data-testid="incomes-sort" value={sort} onChange={(event) => setSort(event.target.value)} className="expenses-select">
            <option value="newest">Más recientes</option>
            <option value="oldest">Más antiguos</option>
          </select>
        </section>

        {filtered.length === 0 ? (
          <div className="expenses-empty" data-testid="incomes-empty-state">
            <img src="/mi-contador-mark.png" alt="" className="empty-brand-mark" aria-hidden="true" />
            <h2>Aún no tienes ingresos registrados.</h2>
            <p>Empieza agregando tu primer ingreso o escaneando un comprobante.</p>
            <button type="button" onClick={openCreate} data-testid="incomes-empty-add-button">Agregar ingreso</button>
          </div>
        ) : (
          <>
            <div className="expenses-table-wrap" data-testid="incomes-table-wrap">
              <table className="expenses-table" data-testid="incomes-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Concepto</th>
                    <th>Categoría</th>
                    <th>Monto</th>
                    <th>Método</th>
                    <th>Estado</th>
                    <th>Factura</th>
                    <th aria-label="Acciones" />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((income) => (
                    <tr key={income.id} data-testid={`income-row-${income.id}`}>
                      <td>{(income.date || income.created_at || "").slice(0, 10)}</td>
                      <td>
                        <b>{income.concept}</b>
                        {income.client && <small>{income.client}</small>}
                      </td>
                      <td><span className="expenses-category-badge incomes-category-badge">{income.category || "Otros"}</span></td>
                      <td><strong className="incomes-amount">{money.format(Number(income.amount || 0))}</strong></td>
                      <td>
                        {income.payment_method === "Efectivo" ? (
                          <span className="incomes-cash-badge" data-testid={`income-cash-badge-${income.id}`} title="Aparece también en Caja">
                            💵 Efectivo
                          </span>
                        ) : (
                          income.payment_method || "—"
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          data-testid={`income-status-${income.id}`}
                          className={`incomes-status ${income.payment_status || "cobrado"}`}
                          onClick={() => cycleStatus(income)}
                          title="Clic para alternar Cobrado / Por cobrar"
                        >
                          {STATUS_LABEL[income.payment_status || "cobrado"]}
                        </button>
                      </td>
                      <td>{income.has_invoice ? "Sí" : "No"}</td>
                      <td className="expenses-actions-cell">
                        <button type="button" data-testid={`income-edit-${income.id}`} onClick={() => openEdit(income)} aria-label="Editar ingreso"><Pencil size={15} /></button>
                        <button type="button" data-testid={`income-delete-${income.id}`} onClick={() => setDeleteTarget(income)} aria-label="Eliminar ingreso"><Trash2 size={15} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="expenses-cards" data-testid="incomes-cards">
              {filtered.map((income) => (
                <article key={income.id} data-testid={`income-card-${income.id}`}>
                  <div>
                    <b>{income.concept}</b>
                    <small>{(income.date || income.created_at || "").slice(0, 10)} · {income.category || "Otros"}</small>
                    {income.payment_method === "Efectivo" && (
                      <span className="incomes-cash-badge" style={{ marginTop: 6, display: "inline-flex" }} data-testid={`income-card-cash-badge-${income.id}`}>
                        💵 Efectivo · en Caja
                      </span>
                    )}
                    <button
                      type="button"
                      data-testid={`income-card-status-${income.id}`}
                      className={`incomes-status ${income.payment_status || "cobrado"}`}
                      onClick={() => cycleStatus(income)}
                      style={{ marginTop: 6 }}
                    >
                      {STATUS_LABEL[income.payment_status || "cobrado"]}
                    </button>
                  </div>
                  <aside>
                    <strong className="incomes-amount">{money.format(Number(income.amount || 0))}</strong>
                    <button type="button" data-testid={`income-card-edit-${income.id}`} onClick={() => openEdit(income)} aria-label="Editar"><Pencil size={14} /></button>
                  </aside>
                </article>
              ))}
            </div>
          </>
        )}

        {modalOpen && (
          <div className="income-modal-overlay" data-testid="income-modal">
            <form className="income-modal income-modal-form" onSubmit={submit} data-testid="income-form">
              <button type="button" className="modal-close" data-testid="income-modal-close" onClick={() => { setModalOpen(false); setEditingId(null); }} aria-label="Cerrar"><X /></button>
              <h2>{editingId ? "Editar Ingreso" : "Añadir Ingreso"}</h2>
              <label>Concepto
                <input data-testid="income-concept-input" value={form.concept} onChange={(event) => setForm({ ...form, concept: event.target.value })} required />
              </label>
              <label>Monto
                <input data-testid="income-amount-input" type="number" min="0.01" step="0.01" value={form.amount} onChange={(event) => setForm({ ...form, amount: event.target.value })} required />
              </label>
              <label>Fecha
                <DatePickerField value={form.date} onChange={(value) => setForm({ ...form, date: value })} testId="income-date-input" />
              </label>
              <label>Categoría
                <select data-testid="income-category-select" value={form.category} onChange={(event) => setForm({ ...form, category: event.target.value })}>
                  {CATEGORIES.map((cat) => <option key={cat}>{cat}</option>)}
                </select>
              </label>
              <label>Método de pago
                <select data-testid="income-payment-select" value={form.payment_method} onChange={(event) => setForm({ ...form, payment_method: event.target.value })}>
                  {PAYMENT_METHODS.map((option) => <option key={option}>{option}</option>)}
                </select>
              </label>
              <label>Estado del cobro
                <select data-testid="income-status-select" value={form.payment_status} onChange={(event) => setForm({ ...form, payment_status: event.target.value })}>
                  <option value="cobrado">Cobrado</option>
                  <option value="por_cobrar">Por cobrar</option>
                </select>
              </label>
              <label>Cliente / Empresa (opcional)
                <AutocompleteInput value={form.client} onChange={(value) => setForm({ ...form, client: value })} options={clientOptions} onCreate={createClient} createLabel="Crear cliente" placeholder="Ej: Cliente Alpha SA..." testId="income-client-input" />
              </label>
              <label>Nota (opcional)
                <textarea data-testid="income-note-input" rows="2" value={form.note} onChange={(event) => setForm({ ...form, note: event.target.value })} />
              </label>
              <label className="expenses-inline-check">
                <input data-testid="income-has-invoice-checkbox" type="checkbox" checked={form.has_invoice} onChange={(event) => setForm({ ...form, has_invoice: event.target.checked })} />
                <span>Tiene factura (CFDI)</span>
              </label>
              {!editingId && (
                <label>Adjuntar archivo (opcional)
                  <input data-testid="income-file-input" name="file" type="file" accept="application/pdf,image/*" />
                </label>
              )}
              {error && <p className="billing-error" data-testid="income-form-error">{error}</p>}
              <button type="submit" className="primary" data-testid="income-save-button">{editingId ? "Guardar cambios" : "Guardar ingreso"}</button>
            </form>
          </div>
        )}

        {deleteTarget && (
          <div className="income-modal-overlay" data-testid="income-delete-modal">
            <div className="income-modal" style={{ maxWidth: 400 }}>
              <h2>¿Eliminar ingreso?</h2>
              <p style={{ color: "#5a666b", margin: "0 0 20px" }}>Se eliminará <b>{deleteTarget.concept}</b> por <b>{money.format(Number(deleteTarget.amount || 0))}</b>. Esta acción no se puede deshacer.</p>
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button type="button" className="client-cancel-button" data-testid="income-delete-cancel" onClick={() => setDeleteTarget(null)}>Cancelar</button>
                <button type="button" className="primary" style={{ background: "#d7565d" }} data-testid="income-delete-confirm" onClick={confirmDelete}>Eliminar</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
